import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Typography, notification } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PageWrapper from '../../../ui/PageWrapper';
import { DictionaryCatalogDtoResponse } from '../../../interfaces/DictionaryCatalogDtoResponse';
import { CounterpartyService } from '../../../services/CounterpartyService';


const CounterpartyAgreementTypesPage = () => {
    const [data, setData] = useState<DictionaryCatalogDtoResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<DictionaryCatalogDtoResponse | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchAgreementTypes();
    }, []);

    const fetchAgreementTypes = async () => {
        setLoading(true);
        try {
            const response = await CounterpartyService.getCounterpartyAgreementTypes();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching data" });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: DictionaryCatalogDtoResponse) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this item?',
            onOk: async () => {
                setLoading(true);
                try {
                    await CounterpartyService.deleteCounterpartyAgreementType(id);
                    notification.success({ message: "Agreement type deleted" });
                    fetchAgreementTypes();
                } catch (error) {
                    notification.error({ message: "Error deleting agreement type" });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingItem) {
                await CounterpartyService.updateCounterpartyAgreementType(editingItem.id, values);
                notification.success({ message: "Agreement type successfully updated" });
            } else {
                await CounterpartyService.createCounterpartyAgreementType(values);
                notification.success({ message: "Agreement type successfully created" });
            }

            setIsModalVisible(false);
            fetchAgreementTypes();
        } catch (error) {
            notification.error({ message: "Error saving agreement type" });
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: 'Name (KZ)', dataIndex: 'name', key: 'name' },
        { title: 'Name (RU)', dataIndex: 'nameRu', key: 'nameRu' },
        { title: 'Name (EN)', dataIndex: 'nameEn', key: 'nameEn' },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: DictionaryCatalogDtoResponse) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Counterparty Agreement Types</Typography.Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
                Add Counterparty Agreement Type
            </Button>
            <Table columns={columns} dataSource={data} loading={loading} rowKey="id" />
            <Modal
                title={editingItem ? 'Edit Counterparty Agreement Type' : 'Add Counterparty Agreement Type'}
                visible={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name (KZ)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameRu" label="Name (RU)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEn" label="Name (EN)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </PageWrapper>
    );
};

export default CounterpartyAgreementTypesPage;

